"use client";

import { useEffect } from "react";
import { useRouter, useParams } from "next/navigation";
import { useForm, Controller } from "react-hook-form";
import { ArrowLeft, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Skeleton } from "@/components/ui/skeleton";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { toast } from "sonner";
import { useApiPost } from "@admin/_lib/api-hooks";
import { useQuery } from "@tanstack/react-query";
import { useSession } from "next-auth/react";

// ─── Types ────────────────────────────────────────────────────────────────────

type ContentType =
  | "live_stream"
  | "video_library"
  | "document"
  | "youtube_video"
  | "announcement"
  | "";

type VideoSource = "Video File" | "YouTube URL" | "Video URL" | "";

interface FormValues {
  content_type: ContentType;
  title: string;
  description: string;
  access_control: "Free" | "Members";
  content_status: "Publish" | "Not Publish";
  priority: string;
  tags: string;
  thumbnail_path: string;

  // Live stream
  stream_source: "0" | "1"; // "0"=embed, "1"=direct
  embed_url: string;
  direct_url: string;
  scheduled_date: string;
  duration: string;
  reminders: boolean;
  reminder_time: string;
  reminder_method: "Push Notification" | "Email" | "";

  // Video library
  video_source: VideoSource;
  file_path: string;
  youtube_url: string;
  video_url: string;

  // Announcement
  display_startdate: string;
  display_enddate: string;
  link: string;
}

const DEFAULT_VALUES: FormValues = {
  content_type: "",
  title: "",
  description: "",
  access_control: "Free",
  content_status: "Publish",
  priority: "",
  tags: "",
  thumbnail_path: "",
  stream_source: "0",
  embed_url: "",
  direct_url: "",
  scheduled_date: "",
  duration: "",
  reminders: false,
  reminder_time: "",
  reminder_method: "",
  video_source: "",
  file_path: "",
  youtube_url: "",
  video_url: "",
  display_startdate: "",
  display_enddate: "",
  link: "",
};

const BASE_URL =
  process.env.NEXT_PUBLIC_API_CLOUDFRONT || "https://api.divineinfinitebeing.com/";

// ─── Field helpers ────────────────────────────────────────────────────────────

function FieldWrapper({
  label,
  required,
  error,
  children,
}: {
  label: string;
  required?: boolean;
  error?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="space-y-1.5">
      <Label className="text-sm text-muted-foreground">
        {label}
        {required && <span className="text-destructive ml-1">*</span>}
      </Label>
      {children}
      {error && <p className="text-xs text-destructive">{error}</p>}
    </div>
  );
}

function SelectNative({
  value,
  onChange,
  options,
  placeholder,
}: {
  value: string;
  onChange: (v: string) => void;
  options: { value: string; label: string }[];
  placeholder?: string;
}) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
    >
      {placeholder && (
        <option value="" disabled>
          {placeholder}
        </option>
      )}
      {options.map((o) => (
        <option key={o.value} value={o.value}>
          {o.label}
        </option>
      ))}
    </select>
  );
}

// ─── Shared field blocks ──────────────────────────────────────────────────────

function CommonMetaFields({
  register,
  errors,
  control,
}: {
  register: ReturnType<typeof useForm<FormValues>>["register"];
  errors: ReturnType<typeof useForm<FormValues>>["formState"]["errors"];
  control: ReturnType<typeof useForm<FormValues>>["control"];
}) {
  return (
    <>
      <FieldWrapper label="Title" required error={errors.title?.message}>
        <Input {...register("title", { required: "Title is required" })} placeholder="Enter title" />
      </FieldWrapper>

      <FieldWrapper label="Description">
        <Textarea {...register("description")} rows={4} placeholder="Enter description" />
      </FieldWrapper>

      <FieldWrapper label="Tags">
        <Input {...register("tags")} placeholder="Comma-separated tags" />
      </FieldWrapper>

      <FieldWrapper label="Thumbnail URL">
        <Input {...register("thumbnail_path")} placeholder="https://..." />
        <p className="text-xs text-muted-foreground">Paste CDN/S3 URL. Bucket upload deferred.</p>
      </FieldWrapper>

      <FieldWrapper label="Access Control">
        <Controller
          name="access_control"
          control={control}
          render={({ field }) => (
            <SelectNative
              value={field.value}
              onChange={field.onChange}
              options={[
                { value: "Free", label: "Free" },
                { value: "Members", label: "Members Only" },
              ]}
            />
          )}
        />
      </FieldWrapper>

      <FieldWrapper label="Priority">
        <Input {...register("priority")} type="number" placeholder="0" />
      </FieldWrapper>

      <FieldWrapper label="Content Status">
        <Controller
          name="content_status"
          control={control}
          render={({ field }) => (
            <SelectNative
              value={field.value}
              onChange={field.onChange}
              options={[
                { value: "Publish", label: "Publish" },
                { value: "Not Publish", label: "Not Publish" },
              ]}
            />
          )}
        />
      </FieldWrapper>
    </>
  );
}

// ─── Branch: Live Stream ──────────────────────────────────────────────────────

function LiveStreamFields({
  register,
  errors,
  control,
  watch,
}: {
  register: ReturnType<typeof useForm<FormValues>>["register"];
  errors: ReturnType<typeof useForm<FormValues>>["formState"]["errors"];
  control: ReturnType<typeof useForm<FormValues>>["control"];
  watch: ReturnType<typeof useForm<FormValues>>["watch"];
}) {
  const streamSource = watch("stream_source");
  const reminders = watch("reminders");

  return (
    <div className="space-y-4 border-l-2 pl-4" style={{ borderColor: "hsl(240 20% 26%)" }}>
      <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        Live Stream Settings
      </p>

      <FieldWrapper label="Stream Source">
        <Controller
          name="stream_source"
          control={control}
          render={({ field }) => (
            <RadioGroup
              value={field.value}
              onValueChange={field.onChange}
              className="flex gap-6"
            >
              <div className="flex items-center gap-2">
                <RadioGroupItem value="0" id="src-embed" />
                <Label htmlFor="src-embed" className="font-normal cursor-pointer">Embed URL</Label>
              </div>
              <div className="flex items-center gap-2">
                <RadioGroupItem value="1" id="src-direct" />
                <Label htmlFor="src-direct" className="font-normal cursor-pointer">Direct URL</Label>
              </div>
            </RadioGroup>
          )}
        />
      </FieldWrapper>

      {streamSource === "0" ? (
        <FieldWrapper label="Embed URL" error={errors.embed_url?.message}>
          <Input
            {...register("embed_url")}
            placeholder="https://..."
          />
        </FieldWrapper>
      ) : (
        <FieldWrapper label="Direct URL" error={errors.direct_url?.message}>
          <Input
            {...register("direct_url")}
            placeholder="https://..."
          />
        </FieldWrapper>
      )}

      <FieldWrapper label="Scheduled Date">
        <Input {...register("scheduled_date")} type="date" />
      </FieldWrapper>

      <FieldWrapper label="Duration (minutes)">
        <Input {...register("duration")} type="number" placeholder="60" />
      </FieldWrapper>

      <div className="flex items-center gap-2 pt-1">
        <Controller
          name="reminders"
          control={control}
          render={({ field }) => (
            <Checkbox
              id="reminders"
              checked={field.value}
              onCheckedChange={field.onChange}
            />
          )}
        />
        <Label htmlFor="reminders" className="font-normal cursor-pointer text-sm">
          Enable Reminders
        </Label>
      </div>

      {reminders && (
        <div className="space-y-4 pl-4 border-l" style={{ borderColor: "hsl(240 20% 30%)" }}>
          <FieldWrapper label="Reminder Time (minutes before)">
            <Input {...register("reminder_time")} type="number" placeholder="30" />
          </FieldWrapper>

          <FieldWrapper label="Reminder Method">
            <Controller
              name="reminder_method"
              control={control}
              render={({ field }) => (
                <SelectNative
                  value={field.value}
                  onChange={field.onChange}
                  placeholder="Select method"
                  options={[
                    { value: "Push Notification", label: "Push Notification" },
                    { value: "Email", label: "Email" },
                  ]}
                />
              )}
            />
          </FieldWrapper>
        </div>
      )}
    </div>
  );
}

// ─── Branch: Video Library ────────────────────────────────────────────────────

function VideoLibraryFields({
  register,
  control,
  watch,
}: {
  register: ReturnType<typeof useForm<FormValues>>["register"];
  control: ReturnType<typeof useForm<FormValues>>["control"];
  watch: ReturnType<typeof useForm<FormValues>>["watch"];
}) {
  const videoSource = watch("video_source");

  return (
    <div className="space-y-4 border-l-2 pl-4" style={{ borderColor: "hsl(240 20% 26%)" }}>
      <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        Video Library Settings
      </p>

      <FieldWrapper label="Video Source">
        <Controller
          name="video_source"
          control={control}
          render={({ field }) => (
            <SelectNative
              value={field.value}
              onChange={field.onChange}
              placeholder="Select video source"
              options={[
                { value: "Video File", label: "Video File (URL)" },
                { value: "YouTube URL", label: "YouTube URL" },
                { value: "Video URL", label: "Direct Video URL" },
              ]}
            />
          )}
        />
      </FieldWrapper>

      {videoSource === "Video File" && (
        <FieldWrapper label="Video File URL">
          <Input {...register("file_path")} placeholder="https://cdn.example.com/video.mp4" />
          <p className="text-xs text-muted-foreground">Paste CDN/S3 URL. Bucket upload deferred.</p>
        </FieldWrapper>
      )}

      {videoSource === "YouTube URL" && (
        <FieldWrapper label="YouTube URL">
          <Input
            {...register("youtube_url")}
            placeholder="https://www.youtube.com/watch?v=..."
          />
        </FieldWrapper>
      )}

      {videoSource === "Video URL" && (
        <FieldWrapper label="Direct Video URL">
          <Input {...register("video_url")} placeholder="https://..." />
        </FieldWrapper>
      )}
    </div>
  );
}

// ─── Branch: Document ─────────────────────────────────────────────────────────

function DocumentFields({
  register,
}: {
  register: ReturnType<typeof useForm<FormValues>>["register"];
}) {
  return (
    <div className="space-y-4 border-l-2 pl-4" style={{ borderColor: "hsl(240 20% 26%)" }}>
      <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        Document Settings
      </p>

      <FieldWrapper label="Document File URL">
        <Input {...register("file_path")} placeholder="https://cdn.example.com/doc.pdf" />
        <p className="text-xs text-muted-foreground">Paste CDN/S3 URL. Bucket upload deferred.</p>
      </FieldWrapper>
    </div>
  );
}

// ─── Branch: YouTube Video ────────────────────────────────────────────────────

function YouTubeVideoFields({
  register,
}: {
  register: ReturnType<typeof useForm<FormValues>>["register"];
}) {
  return (
    <div className="space-y-4 border-l-2 pl-4" style={{ borderColor: "hsl(240 20% 26%)" }}>
      <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        YouTube Video Settings
      </p>

      <FieldWrapper label="YouTube URL">
        <Input
          {...register("youtube_url")}
          placeholder="https://www.youtube.com/watch?v=..."
        />
      </FieldWrapper>
    </div>
  );
}

// ─── Branch: Announcement ─────────────────────────────────────────────────────

function AnnouncementFields({
  register,
}: {
  register: ReturnType<typeof useForm<FormValues>>["register"];
}) {
  return (
    <div className="space-y-4 border-l-2 pl-4" style={{ borderColor: "hsl(240 20% 26%)" }}>
      <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        Announcement Settings
      </p>

      <FieldWrapper label="Display Start Date">
        <Input {...register("display_startdate")} type="date" />
      </FieldWrapper>

      <FieldWrapper label="Display End Date">
        <Input {...register("display_enddate")} type="date" />
      </FieldWrapper>

      <FieldWrapper label="Link (optional)">
        <Input {...register("link")} placeholder="https://..." />
      </FieldWrapper>
    </div>
  );
}

// ─── Main form ────────────────────────────────────────────────────────────────

export function PerennialContentForm() {
  const router = useRouter();
  const params = useParams();
  const id = params?.id as string | undefined;
  const isEdit = !!id;
  const { data: session } = useSession();

  const {
    control,
    handleSubmit,
    reset,
    register,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({ defaultValues: DEFAULT_VALUES });

  const contentType = watch("content_type");

  // ── Edit fetch ──────────────────────────────────────────────────────────────
  const { data: record, isLoading: isFetching } = useQuery({
    queryKey: ["content/content-fetch", id],
    queryFn: async () => {
      const res = await fetch(`${BASE_URL}content/content-fetch`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${session?.token}`,
        },
        body: JSON.stringify({ _id: id }),
      });
      const data = await res.json();
      return data.results?.res ?? data.results ?? data;
    },
    enabled: isEdit && !!session?.token,
  });

  useEffect(() => {
    if (!record) return;
    const r = record as Record<string, unknown>;
    reset({
      content_type: (r.content_type as ContentType) ?? "",
      title: String(r.title ?? ""),
      description: String(r.description ?? ""),
      access_control: (r.access_control as "Free" | "Members") ?? "Free",
      content_status: (r.content_status as "Publish" | "Not Publish") ?? "Publish",
      priority: r.priority != null ? String(r.priority) : "",
      tags: String(r.tags ?? ""),
      thumbnail_path: String(r.thumbnail_path ?? ""),
      // Live stream
      stream_source: (r.stream_source === 1 || r.stream_source === "1") ? "1" : "0",
      embed_url: String(r.embed_url ?? ""),
      direct_url: String(r.direct_url ?? ""),
      scheduled_date: r.scheduled_date ? String(r.scheduled_date).slice(0, 10) : "",
      duration: r.duration != null ? String(r.duration) : "",
      reminders: !!(r.reminders),
      reminder_time: r.reminder_time != null ? String(r.reminder_time) : "",
      reminder_method: (r.reminder_method as "Push Notification" | "Email") ?? "",
      // Video library
      video_source: (r.video_source as VideoSource) ?? "",
      file_path: String(r.file_path ?? ""),
      youtube_url: String(r.youtube_url ?? ""),
      video_url: String(r.video_url ?? ""),
      // Announcement
      display_startdate: r.display_startdate ? String(r.display_startdate).slice(0, 10) : "",
      display_enddate: r.display_enddate ? String(r.display_enddate).slice(0, 10) : "",
      link: String(r.link ?? ""),
    });
  }, [record, reset]);

  // ── Mutations ───────────────────────────────────────────────────────────────
  const addMutation = useApiPost("content/content-add", {
    invalidateKeys: ["content/content-list", "content/content-list-count"],
  });
  const editMutation = useApiPost("content/content-update", {
    invalidateKeys: ["content/content-list", "content/content-list-count"],
  });
  const mutation = isEdit ? editMutation : addMutation;

  // ── Submit ──────────────────────────────────────────────────────────────────
  const onSubmit = (values: FormValues) => {
    if (!values.content_type) {
      toast.error("Please select a content type");
      return;
    }

    // Base payload — common across all types
    const base = {
      content_type: values.content_type,
      title: values.title,
      description: values.description,
      access_control: values.access_control,
      content_status: values.content_status,
      priority: values.priority !== "" ? parseFloat(values.priority) : undefined,
      tags: values.tags,
      ...(values.thumbnail_path ? { thumbnail_path: values.thumbnail_path } : {}),
      ...(isEdit ? { _id: id } : {}),
    };

    // Branch-specific payload
    let branchPayload: Record<string, unknown> = {};

    if (values.content_type === "live_stream") {
      const streamSrc = values.stream_source;
      branchPayload = {
        stream_source: Number(streamSrc),
        ...(streamSrc === "0" && values.embed_url ? { embed_url: values.embed_url } : {}),
        ...(streamSrc === "1" && values.direct_url ? { direct_url: values.direct_url } : {}),
        ...(values.scheduled_date ? { scheduled_date: values.scheduled_date } : {}),
        ...(values.duration !== "" ? { duration: Number(values.duration) } : {}),
        reminders: values.reminders,
        ...(values.reminders && values.reminder_time !== ""
          ? { reminder_time: Number(values.reminder_time) }
          : {}),
        ...(values.reminders && values.reminder_method
          ? { reminder_method: values.reminder_method }
          : {}),
      };
    } else if (values.content_type === "video_library") {
      branchPayload = {
        video_source: values.video_source,
        ...(values.video_source === "Video File" && values.file_path
          ? { file_path: values.file_path }
          : {}),
        ...(values.video_source === "YouTube URL" && values.youtube_url
          ? { youtube_url: values.youtube_url }
          : {}),
        ...(values.video_source === "Video URL" && values.video_url
          ? { video_url: values.video_url }
          : {}),
      };
    } else if (values.content_type === "document") {
      branchPayload = {
        ...(values.file_path ? { file_path: values.file_path } : {}),
      };
    } else if (values.content_type === "youtube_video") {
      branchPayload = {
        ...(values.youtube_url ? { youtube_url: values.youtube_url } : {}),
      };
    } else if (values.content_type === "announcement") {
      branchPayload = {
        ...(values.display_startdate ? { display_startdate: values.display_startdate } : {}),
        ...(values.display_enddate ? { display_enddate: values.display_enddate } : {}),
        ...(values.link ? { link: values.link } : {}),
      };
    }

    const payload = { ...base, ...branchPayload };

    mutation.mutate(payload, {
      onSuccess: () => {
        toast.success(isEdit ? "Content updated successfully" : "Content added successfully");
        router.push("/admin/perennial-content");
      },
      onError: (err: unknown) => {
        const msg = err instanceof Error ? err.message : "Something went wrong";
        toast.error(msg);
      },
    });
  };

  const pageTitle = isEdit ? "Edit Perennial Content" : "Add Perennial Content";

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="icon" onClick={() => router.push("/admin/perennial-content")}>
          <ArrowLeft className="size-4" />
        </Button>
        <div>
          <h1 className="text-xl font-semibold">{pageTitle}</h1>
          <p className="text-sm text-muted-foreground">Perennial Mandalism dashboard content</p>
        </div>
      </div>

      <Card style={{ borderColor: "hsl(240 20% 22%)", background: "hsl(240 20% 13%)" }}>
        <CardHeader>
          <CardTitle className="text-base">{pageTitle}</CardTitle>
        </CardHeader>
        <CardContent>
          {isEdit && isFetching ? (
            <div className="space-y-4">
              {[...Array(6)].map((_, i) => (
                <Skeleton key={i} className="h-10 w-full" />
              ))}
            </div>
          ) : (
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
              {/* ── Content Type selector — always first ── */}
              <FieldWrapper label="Content Type" required error={errors.content_type?.message}>
                <Controller
                  name="content_type"
                  control={control}
                  rules={{ required: "Content type is required" }}
                  render={({ field }) => (
                    <SelectNative
                      value={field.value}
                      onChange={field.onChange}
                      placeholder="Select content type"
                      options={[
                        { value: "live_stream", label: "Live Stream" },
                        { value: "video_library", label: "Video Library" },
                        { value: "document", label: "Document" },
                        { value: "youtube_video", label: "YouTube Video" },
                        { value: "announcement", label: "Announcement" },
                      ]}
                    />
                  )}
                />
              </FieldWrapper>

              {/* ── Branch-specific fields ── */}
              {contentType === "live_stream" && (
                <LiveStreamFields
                  register={register}
                  errors={errors}
                  control={control}
                  watch={watch}
                />
              )}

              {contentType === "video_library" && (
                <VideoLibraryFields
                  register={register}
                  control={control}
                  watch={watch}
                />
              )}

              {contentType === "document" && (
                <DocumentFields register={register} />
              )}

              {contentType === "youtube_video" && (
                <YouTubeVideoFields register={register} />
              )}

              {contentType === "announcement" && (
                <AnnouncementFields register={register} />
              )}

              {/* ── Common metadata — shown once a type is selected ── */}
              {contentType !== "" && (
                <CommonMetaFields
                  register={register}
                  errors={errors}
                  control={control}
                />
              )}

              {/* ── Actions ── */}
              {contentType !== "" && (
                <div className="flex gap-3 pt-2">
                  <Button
                    type="submit"
                    disabled={isSubmitting || mutation.isPending}
                    style={{ background: "#ffa800", color: "#000" }}
                  >
                    {(isSubmitting || mutation.isPending) && (
                      <Loader2 className="size-4 mr-2 animate-spin" />
                    )}
                    {isEdit ? "Update Content" : "Add Content"}
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => router.push("/admin/perennial-content")}
                  >
                    Cancel
                  </Button>
                </div>
              )}
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
